import React, { useCallback } from "react";
import { AspectRatio, Box, Image, Text } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { useFormatDateTime } from "@bookingcom/flights-core";

import { SPACER_PADDED } from "@flights/constants";
import { UIPopularFlightComponent, UIPopularFlightInfo } from "@flights/types";
import { getRoundtripSearchUrl } from "../../../utils/search-urls";
import { getMarketingTrackingVariable, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { getCurrencySymbolByPointOfSale } from "@flights/content-universal";
import styles from "./PopularFlightsItem.module.css";

import useGlobalContext from "../../../hooks/useGlobalContext";
import useFormatPrice from "../../../hooks/useFormatPrice";
import useCreateUrl from "../../../hooks/useCreateUrl";
import useTrackFlightsWebUseOfferPos from "../../../hooks/useTrackFlightsWebUseOfferPosExp";
import useLandingPageEvents from "../../../hooks/c360/useLandingPageEvents";
import { useLocation } from "react-router-dom";

interface Props {
  flight: UIPopularFlightInfo;
  index: number;
  component: UIPopularFlightComponent;
  isMobile?: boolean;
}

interface Image {
  src: string;
  alt: string;
}

const getImage = (toCityImageUrl: string, toCityName: string): Image => {
  return {
    src: toCityImageUrl,
    alt: toCityName
  };
};

const getSearchUrl = (flight: UIPopularFlightInfo, prevSearchParams?: string): string => {
  return getRoundtripSearchUrl(
    {
      fromIata: flight.fromCityIata,
      toIata: flight.toCityIata,
      departureDate: flight.tripStartDate,
      returnDate: flight.tripEndDate
    },
    "flights_index_pf",
    prevSearchParams
  );
};

const FlightsNearbyItem: React.FunctionComponent<Props> = ({ flight, index, component }) => {
  const i18n = useI18n();
  const { formats } = useFormatDateTime(i18n);
  const { ipCountry } = useGlobalContext();
  const flightsWebUseOfferPosExp = useTrackFlightsWebUseOfferPos();
  const metaSalesCountry = flightsWebUseOfferPosExp.isVariant && getMarketingTrackingVariable("salesCountry");
  const { createUrlWithApiPrefix } = useCreateUrl();
  const { formatPrice } = useFormatPrice();
  const trackLandingPageEvents = useLandingPageEvents();
  const location = useLocation();

  const routeName = i18n.trans(
    t("seo_flights_new_index_lp_cheap_flights_meta_flight_route", {
      variables: { city_name_origin: flight.fromCityNameLocalised, city_name_destination: flight.toCityNameLocalised }
    })
  );

  const fromDateFormatted = formats.flightDate(flight.tripStartDate);
  const toDateFormatted = formats.flightDate(flight.tripEndDate);

  const flightParams =
    `${fromDateFormatted} - ${toDateFormatted}` +
    SPACER_PADDED +
    `${i18n.trans(t("seo_flights_new_index_lp_cheap_flights_meta_round_trip"))}`;

  // Optionally show price information.
  const currency = getCurrencySymbolByPointOfSale(
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    (metaSalesCountry || ipCountry).toLowerCase() as string,
    isFeatureRunningClientSide("FLIGHTS_WEB_CAT_BG_EUR_CURRENCY")
  );
  const fromPriceFormated = flight.price?.units
    ? formatPrice({ currencyCode: currency, units: flight.price?.units, nanos: 0 })
    : "";
  const getFromPrice = () =>
    `${i18n.trans(
      t("seo_flights_new_index_lp_cheap_flights_meta_flight_from_price", {
        variables: { flight_price: fromPriceFormated, num_exception: 1 }
      })
    )}`;

  const url = createUrlWithApiPrefix(getSearchUrl(flight, location?.search));
  const handleClick = useCallback(() => {
    trackLandingPageEvents("click", component, { ...flight, position: index + 1, url });
  }, [trackLandingPageEvents, component, flight, index, url]);

  const imageUrl = flight.toCityImageUrl;

  return (
    <a className={styles.anchorContainer} href={url} rel="nofollow" onClick={handleClick} onAuxClick={handleClick}>
      <Box padding={0}>
        <AspectRatio ratio="4:3">
          <Image className={styles.image} {...getImage(imageUrl || "", routeName)} fallback="image" />
        </AspectRatio>
        <div className={styles.textContainer}>
          <Text className={styles.textLine} variant="strong_1">
            {routeName}
          </Text>
          <Text className={styles.textLine} variant="body_2" color="neutral_alt">
            {flightParams}
          </Text>
          {fromPriceFormated && (
            <Text className={styles.textLine} variant="body_2" color="neutral_alt">
              {getFromPrice()}
            </Text>
          )}
        </div>
      </Box>
    </a>
  );
};
export default FlightsNearbyItem;
